import api from "@/services/axios";

/**
 * Permission API - Integrates with backend permission assignment API
 * Handles loading, assigning, and caching user permissions
 */

/**
 * Response from backend when loading user permissions
 */
export interface LoadUserPermissionsResponse {
  success: boolean;
  message: string;
  data: {
    userId: string;
    tenantId: string;
    permissionCodes: string[];
    loadedAt: string;
  };
  statusCode: number;
  errors?: Array<{
    code: string;
    message: string;
    type: string;
  }>;
}

/**
 * Response from backend when assigning permissions
 */
export interface PermissionAssignmentResponse {
  success: boolean;
  message: string;
  data: string; // assignmentId (Guid)
  statusCode: number;
  errors?: Array<{
    code: string;
    message: string;
    type: string;
  }>;
}

/**
 * Response from backend when removing permissions
 */
export interface PermissionRemovalResponse {
  success: boolean;
  message: string;
  data: boolean;
  statusCode: number;
  errors?:
    | Array<{
        code: string;
        message: string;
        type: string;
      }>
    | undefined;
}

/**
 * Request payload for loading user permissions
 */
export interface LoadUserPermissionsRequest {
  userId: string;
  tenantId: string;
}

/**
 * Request payload for assigning tenant-level permissions
 */
export interface AssignTenantPermissionRequest {
  tenantId: string;
  userId: string;
  permissionCode: string;
  isGranted: boolean;
  assignedByUserId?: string;
}

/**
 * Request payload for assigning user-level permissions
 */
export interface AssignUserPermissionRequest {
  userId: string;
  tenantId: string;
  permissionCode: string;
  isGranted: boolean;
  assignedByUserId?: string;
}

/**
 * Request payload for removing tenant-level permissions
 */
export interface RemoveTenantPermissionRequest {
  tenantId: string;
  userId: string;
  permissionCode: string;
}

/**
 * Request payload for removing user-level permissions
 */
export interface RemoveUserPermissionRequest {
  userId: string;
  tenantId: string;
  permissionCode: string;
}

/**
 * Request payload for refreshing permission cache
 */
export interface RefreshPermissionCacheRequest {
  userId: string;
  tenantId: string;
}

/**
 * Load all permissions for a user in a specific tenant
 * Resolves 5-level permission hierarchy: User → Tenant → Role → SystemRole → Default
 */
export async function loadUserPermissions(
  userId: string,
  tenantId: string
): Promise<LoadUserPermissionsResponse> {
  const response = await api.post<LoadUserPermissionsResponse>(
    "/user-access/permission-assignments/load-permissions",
    {
      userId,
      tenantId,
    } as LoadUserPermissionsRequest
  );
  return response.data;
}

/**
 * Assign a permission at the tenant level (affects multiple users in that tenant)
 */
export async function assignTenantPermission(
  tenantId: string,
  userId: string,
  permissionCode: string,
  isGranted: boolean,
  assignedByUserId?: string
): Promise<PermissionAssignmentResponse> {
  const response = await api.post<PermissionAssignmentResponse>(
    "/user-access/permission-assignments/tenant-assignment",
    {
      tenantId,
      userId,
      permissionCode,
      isGranted,
      assignedByUserId,
    } as AssignTenantPermissionRequest
  );
  return response.data;
}

/**
 * Assign a permission at the user level (specific to one user)
 */
export async function assignUserPermission(
  userId: string,
  tenantId: string,
  permissionCode: string,
  isGranted: boolean,
  assignedByUserId?: string
): Promise<PermissionAssignmentResponse> {
  const response = await api.post<PermissionAssignmentResponse>(
    "/user-access/permission-assignments/user-assignment",
    {
      userId,
      tenantId,
      permissionCode,
      isGranted,
      assignedByUserId,
    } as AssignUserPermissionRequest
  );
  return response.data;
}

/**
 * Remove a tenant-level permission assignment
 */
export async function removeTenantPermission(
  tenantId: string,
  userId: string,
  permissionCode: string
): Promise<PermissionRemovalResponse> {
  const response = await api.post<PermissionRemovalResponse>(
    "/user-access/permission-assignments/remove-tenant-assignment",
    {
      tenantId,
      userId,
      permissionCode,
    } as RemoveTenantPermissionRequest
  );
  return response.data;
}

/**
 * Remove a user-level permission assignment
 */
export async function removeUserPermission(
  userId: string,
  tenantId: string,
  permissionCode: string
): Promise<PermissionRemovalResponse> {
  const response = await api.post<PermissionRemovalResponse>(
    "/user-access/permission-assignments/remove-user-assignment",
    {
      userId,
      tenantId,
      permissionCode,
    } as RemoveUserPermissionRequest
  );
  return response.data;
}

/**
 * Refresh the permission cache for a user
 * Invalidates the cached permissions and forces a reload on next access
 */
export async function refreshPermissionCache(
  userId: string,
  tenantId: string
): Promise<PermissionRemovalResponse> {
  const response = await api.post<PermissionRemovalResponse>(
    "/user-access/permission-assignments/refresh-cache",
    {
      userId,
      tenantId,
    }
  );
  return response.data;
}

/**
 * Get all permissions for the tenant
 */
export async function getPermissions(params?: { guardianOnly?: boolean }): Promise<any[]> {
  const response = await api.get("/user-access/permissions", {
    params: params?.guardianOnly ? { guardianOnly: true } : undefined,
  });
  return response.data.data || [];
}

/**
 * Assign permissions to a role
 */
export async function assignRolePermissions(params: {
  roleId: string;
  permissionIds: string[];
}): Promise<boolean> {
  const response = await api.put(`/user-access/roles/${params.roleId}/permissions`, {
    permissionIds: params.permissionIds,
  });
  return response.data.success || true;
}

/**
 * Get role details with permissions
 */
export async function getRoleDetails(roleId: string): Promise<any> {
  const response = await api.get(`/user-access/roles/${roleId}`);
  return response.data.data;
}
